<template>
  <div class="pickups-app">
    <!-- Modern Header with Glass Effect -->
    <div class="app-header small-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-text">
            <h1 class="header-title small-title">
              Gestion des Pickups
            </h1>
            <p class="header-subtitle small-subtitle">
              Gérez vos collectes et suivez les enlèvements
            </p>
          </div>
        </div>
        <div class="header-right">
          <div class="stats-card small-stats">
            <div class="stat-item">
              <span class="stat-value">{{ count }}</span>
              <span class="stat-label">Total Pickups</span>
            </div>
          </div>
          <div class="header-actions">
            <button
              v-if="canBulkAssign"
              :disabled="isLoading || selectedPickups.length === 0"
              class="action-btn primary"
              @click="openBulkAssignModal"
            >
              <feather-icon
                icon="UsersIcon"
                size="18"
              />
              Assigner des expéditions
              <span
                v-if="selectedPickups.length"
                class="ml-1"
              >({{ selectedPickups.length }})</span>
            </button>
            <button
              :disabled="isLoading"
              class="action-btn secondary"
              @click="refreshData"
            >
              <feather-icon
                icon="RotateCcwIcon"
                size="18"
              />
              Actualiser
            </button>
            <button
              v-if="user.role === 'supplier'"
              class="action-btn danger-btn"
              @click="$router.push({ name: 'pickup-add' })"
            >
              <feather-icon
                icon="PlusIcon"
                size="18"
              />
              Nouveau Pickup
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modern Search & Filters Panel -->
    <div class="filters-panel">
      <div class="search-section">
        <div class="search-input-wrapper">
          <div class="search-icon">
            <feather-icon
              icon="SearchIcon"
              size="20"
            />
          </div>
          <input
            v-model="filters.reference"
            v-debounce:1000="applyFilters"
            type="text"
            placeholder="Rechercher par numéro de pickup..."
            class="search-input"
          >
          <div
            v-if="filters.reference"
            class="clear-search"
            @click="filters.reference = ''; applyFilters()"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
          </div>
        </div>

        <button
          class="filter-toggle"
          :class="{ active: showAdvancedFilters }"
          @click="showAdvancedFilters = !showAdvancedFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="20"
          />
          Filtres
          <span
            v-if="hasActiveFilters"
            class="filter-count"
          >{{ Object.values(filters).filter(v => v !== null && v !== '' && v !== undefined).length }}</span>
        </button>
      </div>

      <!-- Expandable Filters -->
      <div
        v-show="showAdvancedFilters"
        class="filters-grid"
      >
        <!-- <div class="filter-group">
          <label class="filter-label">
            Statut
            <span
              v-if="filters.status"
              class="filter-active-indicator"
            >●</span>
          </label>
          <select
            v-model="filters.status"
            class="filter-select"
            :class="{ 'filter-active': filters.status }"
            @change="applyFilters"
          >
            <option value="">
              Tous les statuts
            </option>
            <option
              v-for="option in statusSelectOptions"
              :key="option.value"
              :value="option"
            >
              {{ option.text }}
            </option>
          </select>
        </div> -->

        <div
          v-if="user.role === 'admin'"
          class="filter-group"
        >
          <label class="filter-label">
            Fournisseur
            <span
              v-if="filters.supplier"
              class="filter-active-indicator"
            >●</span>
          </label>
          <select
            v-model="filters.supplier"
            class="filter-select"
            :class="{ 'filter-active': filters.supplier }"
            @change="applyFilters"
          >
            <option value="">
              Tous les fournisseurs
            </option>
            <option
              v-for="supplier in suppliers"
              :key="supplier.id"
              :value="supplier"
            >
              {{ supplier.username }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label class="filter-label">
            Date de début
            <span
              v-if="filters.dateFrom"
              class="filter-active-indicator"
            >●</span>
          </label>
          <input
            v-model="filters.dateFrom"
            type="date"
            class="filter-input"
            :class="{ 'filter-active': filters.dateFrom }"
            @change="applyFilters"
          >
        </div>

        <div class="filter-group">
          <label class="filter-label">
            Date de fin
            <span
              v-if="filters.dateTo"
              class="filter-active-indicator"
            >●</span>
          </label>
          <input
            v-model="filters.dateTo"
            type="date"
            class="filter-input"
            :class="{ 'filter-active': filters.dateTo }"
            @change="applyFilters"
          >
        </div>

        <div
          v-if="user.role === 'admin'"
          class="filter-group"
        >
          <label class="filter-label">
            Montant minimum (DT)
            <span
              v-if="filters.minAmount"
              class="filter-active-indicator"
            >●</span>
          </label>
          <input
            v-model="filters.minAmount"
            v-debounce:1000="applyFilters"
            type="number"
            step="0.001"
            placeholder="0.000"
            class="filter-input"
            :class="{ 'filter-active': filters.minAmount }"
          >
        </div>

        <div
          v-if="user.role === 'admin'"
          class="filter-group"
        >
          <label class="filter-label">
            Montant maximum (DT)
            <span
              v-if="filters.maxAmount"
              class="filter-active-indicator"
            >●</span>
          </label>
          <input
            v-model="filters.maxAmount"
            v-debounce:1000="applyFilters"
            type="number"
            step="0.001"
            placeholder="999.999"
            class="filter-input"
            :class="{ 'filter-active': filters.maxAmount }"
          >
        </div>
      </div>

      <!-- Quick Filters -->
      <div class="quick-filters">
        <button
          v-for="filter in quickFilters"
          :key="filter.key"
          class="quick-filter-btn"
          :class="[filter.style || 'filter-default', { 'quick-filter-active': isQuickFilterActive(filter.key) }]"
          @click="applyQuickFilter(filter)"
        >
          <span class="filter-emoji">
            <feather-icon
              :icon="filter.icon"
              size="16"
            />
          </span>
          {{ filter.label }}
          <span
            v-if="isQuickFilterActive(filter.key)"
            class="quick-filter-check"
          >✓</span>
        </button>
      </div>

      <!-- Applied Filters Display -->
      <div
        v-if="hasActiveFilters"
        class="applied-filters"
      >
        <h6 class="applied-filters-title">
          Filtres appliqués:
        </h6>
        <div class="applied-filters-list">
          <span
            v-for="filter in appliedFilters"
            :key="filter.key"
            class="applied-filter-badge"
            @click="removeFilter(filter.key)"
          >
            {{ filter.label }}: {{ filter.value }}
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </span>
        </div>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <!-- No Results Found -->
    <div
      v-if="!isLoading && pickups.length === 0"
      class="text-center py-5"
    >
      <b-card class="shadow-sm no-results-card">
        <div class="mb-3">
          <feather-icon
            icon="SearchIcon"
            size="48"
            class="text-muted"
          />
        </div>
        <h5 class="text-muted mb-3">
          Aucune collecte trouvée
        </h5>
        <p class="text-muted mb-4">
          <span v-if="hasActiveFilters">
            Aucune collecte ne correspond aux critères de recherche actuels.
            <br>
            Essayez de modifier ou supprimer certains filtres.
          </span>
          <span v-else>
            Il n'y a actuellement aucune collecte dans le système.
          </span>
        </p>
        <div v-if="hasActiveFilters">
          <b-button
            variant="outline-primary"
            @click="clearFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-1"
            />
            Réinitialiser les filtres
          </b-button>
        </div>
        <div v-else-if="user.role === 'supplier'">
          <b-button
            :to="{ name: 'pickup-add' }"
            variant="primary"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Créer votre première collecte
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Error State -->
    <div
      v-if="error"
      class="text-center py-5"
    >
      <b-card class="shadow-sm">
        <feather-icon
          icon="AlertCircleIcon"
          size="48"
          class="text-danger mb-3"
        />
        <h5 class="text-danger mb-3">
          Une erreur est survenue
        </h5>
        <p class="text-muted mb-3">
          {{ error }}
        </p>
        <b-button
          variant="primary"
          @click="refreshData"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
            class="mr-1"
          />
          Réessayer
        </b-button>
      </b-card>
    </div>

    <!-- Results Table -->
    <vue-good-table
      v-if="!isLoading && !error && pickups.length > 0"
      ref="pickupTable"
      :columns="tableColumns"
      :rows="pickups"
      :search-options="{ enabled: false }"
      :pagination-options="{ enabled: true, perPage: pageLength }"
      :select-options="selectOptions"
      @on-selected-rows-change="onSelectionChanged"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- ID Column -->
        <div
          v-if="props.column.field === 'id'"
          class="text-nowrap"
        >
          <div class="d-flex align-items-center">
            <!-- <div
              class="status-dot mr-2"
              :class="getStatusClass(props.row.status)"
            /> -->
            <strong>#{{ props.row.id }}</strong>
          </div>
          <small class="text-muted">{{ formatFn (props.row.created_at) }}</small>
        </div>

        <!-- Supplier Column -->
        <div
          v-else-if="props.column.field === 'supplier'"
          class="text-nowrap"
        >
          <b-avatar
            :text="getInitials(props.row.supplier ? props.row.supplier.username : '')"
            variant="light"
            size="sm"
            class="mr-2"
          />
          <div class="d-inline-block">
            <div class="font-weight-semibold">
              {{ props.row.supplier ? props.row.supplier.username : 'N/A' }}
            </div>
            <small class="text-muted">{{ props.row.supplier ? props.row.supplier.email : 'Email non disponible' }}</small>
          </div>
        </div>

        <!-- Orders Count -->
        <div
          v-else-if="props.column.field === 'orders_count'"
          class="text-center"
        >
          <span class="orders-count-pill">
            <span class="orders-count-number">{{ props.row.orders_count || 0 }}</span>
            <span class="orders-count-label">commandes</span>
          </span>
        </div>

        <!-- Financial Summary -->
        <div
          v-else-if="props.column.field === 'financial_summary'"
          class="text-nowrap"
        >
          <div class="mb-1">
            <small class="text-muted">Total TTC:</small>
            <strong class="text-success">{{ formatPrice(props.row.total_price_ttc) }} TND</strong>
          </div>
          <div>
            <small class="text-muted">Livraison:</small>
            <strong class="text-info">{{ formatPrice(props.row.total_price_delivery) }} TND</strong>
          </div>
        </div>

        <!-- Status Column -->
        <span v-else-if="props.column.field === 'status'">
          <b-badge
            :variant="getStatusVariant(props.row.status)"
            class="badge-glow"
          >
            <feather-icon
              :icon="getStatusIcon(props.row.status)"
              class="mr-25"
              size="14"
            />
            {{ getStatusText(props.row.status) }}
          </b-badge>
          <!-- Scan progress for in_progress pickups -->
          <div
            v-if="props.row.status === 'in_progress' && props.row.scanned_orders_count !== undefined"
            class="scan-progress mt-1"
          >
            <small class="text-muted">
              {{ props.row.scanned_orders_count }}/{{ props.row.expected_orders_count }} scannées
            </small>
          </div>
        </span>

        <!-- Assigned To Column -->
        <div
          v-else-if="props.column.field === 'assigned_to'"
          class="text-nowrap"
        >
          <div v-if="props.row.assigned_to">
            <div class="d-flex align-items-center">
              <b-avatar
                :text="getInitials(props.row.assigned_to.username || '')"
                variant="light-primary"
                size="sm"
                class="mr-1"
              />
              <span class="font-weight-semibold">{{ props.row.assigned_to.username }}</span>
              <b-badge
                v-if="canAssignPermission && canAssign(props.row)"
                variant="light-primary"
                class="ml-2 cursor-pointer"
                @click="openAssignModal(props.row)"
              >
                Changer
              </b-badge>
            </div>
            <div v-if="props.row.assigned_at">
              <small class="text-muted">{{ formatFn(props.row.assigned_at) }}</small>
            </div>
            <div
              v-if="props.row.assigned_by"
              class="text-muted"
            >
              <small>Assigné par {{ props.row.assigned_by.username }}</small>
            </div>
            <div
              v-if="props.row.status === 'accepted' && props.row.accepted_at"
              class="text-muted"
            >
              <small>Accepté le {{ formatFn(props.row.accepted_at) }}</small>
            </div>
          </div>
          <div v-else>
            <b-badge
              v-if="canAssignPermission && canAssign(props.row)"
              variant="light-warning"
              class="cursor-pointer"
              @click="openAssignModal(props.row)"
            >
              <feather-icon
                icon="UserPlusIcon"
                size="12"
                class="mr-25"
              />
              Assigner
            </b-badge>
            <span
              v-else
              class="text-muted"
            >Non assigné</span>
          </div>
        </div>

        <!-- Actions Column -->
        <span v-else-if="props.column.field === 'actions'">
          <!-- View -->
          <router-link
            class="text-danger"
            :to="{ name: 'pickup-show', params: { id: props.row.id} }"
          >
            <feather-icon
              :id="`pickup-row-${props.row.id}-show-icon`"
              icon="EyeIcon"
              class="cursor-pointer mr-2"
              size="20"
            />
          </router-link>
          <b-tooltip
            title="Voir les détails"
            class="cursor-pointer"
            :target="`pickup-row-${props.row.id}-show-icon`"
          />

          <feather-icon
            :id="`pickup-row-${props.row.id}-history-icon`"
            icon="ClockIcon"
            class="cursor-pointer mr-2 text-primary"
            size="20"
            @click="openHistoryModal(props.row)"
          />
          <b-tooltip
            title="Voir l'historique"
            class="cursor-pointer"
            :target="`pickup-row-${props.row.id}-history-icon`"
          />

          <b-button
            v-if="canRevokeAcceptance(props.row)"
            :id="`pickup-row-${props.row.id}-revoke-acceptance`"
            variant="outline-danger"
            size="sm"
            class="ml-1"
            :disabled="revokingPickupId === props.row.id"
            @click="revokeAcceptance(props.row)"
          >
            <b-spinner
              v-if="revokingPickupId === props.row.id"
              small
              class="mr-1"
            />
            Révoquer
          </b-button>
          <b-tooltip
            v-if="canRevokeAcceptance(props.row)"
            title="Révoquer l'acceptation"
            class="cursor-pointer"
            :target="`pickup-row-${props.row.id}-revoke-acceptance`"
          />

          <b-button
            v-if="canAcceptPickup(props.row)"
            :id="`pickup-row-${props.row.id}-accept`"
            variant="outline-primary"
            size="sm"
            class="ml-1"
            :disabled="acceptingPickupId === props.row.id"
            @click="acceptPickup(props.row)"
          >
            <b-spinner
              v-if="acceptingPickupId === props.row.id"
              small
              class="mr-1"
            />
            Accepter
          </b-button>
          <b-tooltip
            v-if="canAcceptPickup(props.row)"
            title="Accepter la collecte"
            class="cursor-pointer"
            :target="`pickup-row-${props.row.id}-accept`"
          />

          <b-button
            v-if="canStartPickup(props.row)"
            :id="`pickup-row-${props.row.id}-start`"
            variant="outline-success"
            size="sm"
            class="ml-1"
            :disabled="startingPickupId === props.row.id"
            @click="startPickup(props.row)"
          >
            <b-spinner
              v-if="startingPickupId === props.row.id"
              small
              class="mr-1"
            />
            Démarrer
          </b-button>
          <b-tooltip
            v-if="canStartPickup(props.row)"
            title="Démarrer la collecte"
            class="cursor-pointer"
            :target="`pickup-row-${props.row.id}-start`"
          />

          <!-- Edit -->
          <!-- <router-link
            v-if="canEdit(props.row)"
            class="text-dark"
            :to="{ name: 'pickup-edit', params: { id: props.row.id} }"
          >
            <feather-icon
              :id="`pickup-row-${props.row.id}-edit-icon`"
              icon="EditIcon"
              class="cursor-pointer mr-2"
              size="18"
            />
          </router-link>
          <b-tooltip
            v-if="canEdit(props.row)"
            title="Modifier"
            class="cursor-pointer"
            :target="`pickup-row-${props.row.id}-edit-icon`"
          /> -->

          <!-- Confirm -->
          <!-- <feather-icon
            v-if="canConfirm(props.row)"
            :id="`pickup-row-${props.row.id}-confirm-icon`"
            icon="CheckCircleIcon"
            class="cursor-pointer mr-2 text-success"
            size="18"
            @click="showConfirmDialog(props.row)"
          />
          <b-tooltip
            v-if="canConfirm(props.row)"
            title="Confirmer le pickup"
            class="cursor-pointer"
            :target="`pickup-row-${props.row.id}-confirm-icon`"
          /> -->
        </span>

        <!-- Default -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap pagination-row">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              Affichage 1 à
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value) => {
                props.perPageChanged({currentPerPage:value});
                applyFilters();
              }"
            />
            <span class="text-nowrap"> de {{ count }} entrées</span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="pageNext(currentPage)"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Confirmation Modal -->
    <!-- <b-modal
      v-model="showConfirmationModal"
      title="Confirmer le pickup"
      title-class="text-danger"
      :ok-disabled="isConfirming"
      :cancel-disabled="isConfirming"
      :hide-header-close="isConfirming"
      no-close-on-backdrop
      no-close-on-esc
      @ok="confirmPickup"
      @cancel="cancelConfirmation"
    >
      <template #modal-ok>
        <b-spinner
          v-if="isConfirming"
          small
          class="mr-2"
        />
        {{ isConfirming ? 'Confirmation...' : 'Confirmer' }}
      </template>

      <div class="text-center">
        <feather-icon
          icon="AlertTriangleIcon"
          size="48"
          class="text-warning mb-3"
        />
        <h5 class="mb-3">
          Êtes-vous sûr de vouloir confirmer ce pickup ?
        </h5>
        <p class="text-muted mb-3">
          Une fois confirmé, vous ne pourrez plus modifier les commandes ou la plupart des détails de ce pickup.
        </p>
        <div
          v-if="pickupToConfirm"
          class="bg-light p-3 rounded mb-3"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="font-weight-bold">Pickup #{{ pickupToConfirm.id }}</span>
            <b-badge
              :variant="getStatusVariant(pickupToConfirm.status)"
              class="badge-glow"
            >
              {{ getStatusText(pickupToConfirm.status) }}
            </b-badge>
          </div>
          <div class="d-flex justify-content-between">
            <span>Commandes:</span>
            <span class="font-weight-bold">{{ pickupToConfirm.orders_count }}</span>
          </div>
          <div class="d-flex justify-content-between">
            <span>Total TTC:</span>
            <span class="font-weight-bold text-success">{{ formatPrice(pickupToConfirm.total_price_ttc) }} TND</span>
          </div>
        </div>
        <small class="text-muted">
          Cette action est irréversible pour les fournisseurs.
        </small>
      </div>
    </b-modal> -->

    <!-- Bulk Confirmation Modal -->
    <!-- <b-modal
      v-model="showBulkConfirmationModal"
      title="Confirmation en lot"
      title-class="text-danger"
      :ok-disabled="isBulkConfirming"
      :cancel-disabled="isBulkConfirming"
      :hide-header-close="isBulkConfirming"
      no-close-on-backdrop
      no-close-on-esc
      @ok="bulkConfirmPickups"
      @cancel="cancelBulkConfirmation"
    >
      <template #modal-ok>
        <b-spinner
          v-if="isBulkConfirming"
          small
          class="mr-2"
        />
        {{ isBulkConfirming ? 'Confirmation...' : 'Confirmer tout' }}
      </template>

      <div class="text-center">
        <feather-icon
          icon="AlertTriangleIcon"
          size="48"
          class="text-warning mb-3"
        />
        <h5 class="mb-3">
          Confirmer {{ confirmablePickups.length }} pickup(s) ?
        </h5>
        <p class="text-muted mb-3">
          Une fois confirmés, vous ne pourrez plus modifier les commandes ou la plupart des détails de ces pickups.
        </p>

        <div class="bg-light p-3 rounded mb-3">
          <h6 class="font-weight-bold mb-2">
            Pickups à confirmer:
          </h6>
          <div class="bulk-confirm-list">
            <div
              v-for="pickup in confirmablePickups"
              :key="pickup.id"
              class="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
            >
              <div class="text-left">
                <span class="font-weight-bold">Expédition #{{ pickup.id }}</span>
                <br>
                <small class="text-muted">{{ pickup.orders_count }} commandes</small>
              </div>
              <div class="text-right">
                <div class="font-weight-bold text-success">
                  {{ formatPrice(pickup.total_price_ttc) }} TND
                </div>
                <b-badge
                  :variant="getStatusVariant(pickup.status)"
                  class="badge-glow"
                >
                  {{ getStatusText(pickup.status) }}
                </b-badge>
              </div>
            </div>
          </div>
          <hr>
          <div class="d-flex justify-content-between font-weight-bold">
            <span>Total:</span>
            <span class="text-success">{{ formatPrice(bulkConfirmTotal) }} TND</span>
          </div>
        </div>

        <div
          v-if="nonConfirmablePickups.length > 0"
          class="bg-warning p-3 rounded mb-3"
        >
          <h6 class="font-weight-bold mb-2">
            ⚠️ Pickups non confirmables:
          </h6>
          <div
            v-for="pickup in nonConfirmablePickups"
            :key="pickup.id"
            class="d-flex justify-content-between align-items-center mb-1"
          >
            <span>Pickup #{{ pickup.id }}</span>
            <small class="text-muted">{{ getReasonNotConfirmable(pickup) }}</small>
          </div>
        </div>

        <small class="text-muted">
          Cette action est irréversible pour les fournisseurs.
        </small>
      </div>
    </b-modal> -->

    <!-- Summary Card -->
    <b-card
      v-if="!isLoading && !error && pickups.length > 0"
      class="mt-3"
      header="Résumé des Pickups"
    >
      <b-row class="mt-1">
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-primary">
              {{ count }}
            </h4>
            <p class="text-muted">
              Total Pickups
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-success">
              {{ totalTTC }} TND
            </h4>
            <p class="text-muted">
              Total TTC
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-info">
              {{ totalOrders }}
            </h4>
            <p class="text-muted">
              Total Commandes
            </p>
          </div>
        </b-col>
        <b-col md="3">
          <div class="text-center">
            <h4 class="text-warning">
              {{ averagePickupValue }} TND
            </h4>
            <p class="text-muted">
              Valeur Moyenne
            </p>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Driver Assignment Modal -->
    <b-modal
      v-model="showAssignModal"
      title="Assigner un livreur"
      title-class="text-primary"
      ok-title="Assigner"
      ok-variant="primary"
      cancel-title="Annuler"
      cancel-variant="outline-secondary"
      :ok-disabled="!selectedDriver || isAssigning || isSameAssigneeSelected"
      :cancel-disabled="isAssigning"
      no-close-on-backdrop
      @ok.prevent="assignDriver"
      @cancel="closeAssignModal"
    >
      <div v-if="pickupToAssign">
        <div class="bg-light p-3 rounded mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="font-weight-bold">Pickup #{{ pickupToAssign.id }}</span>
            <b-badge :variant="getStatusVariant(pickupToAssign.status)">
              {{ getStatusText(pickupToAssign.status) }}
            </b-badge>
          </div>
          <div class="d-flex justify-content-between">
            <span>Fournisseur:</span>
            <span class="font-weight-bold">{{ (pickupToAssign.supplier && pickupToAssign.supplier.username) || 'N/A' }}</span>
          </div>
          <div class="d-flex justify-content-between">
            <span>Commandes:</span>
            <span class="font-weight-bold">{{ pickupToAssign.orders_count || 0 }}</span>
          </div>
        </div>

        <div class="form-group">
          <label
            for="driver-select"
            class="font-weight-bold"
          >Sélectionner un livreur</label>
          <div
            v-if="isLoadingDrivers"
            class="text-center py-3"
          >
            <b-spinner
              variant="primary"
              small
            />
            <span class="ml-2">Chargement des livreurs...</span>
          </div>
          <select
            v-else
            id="driver-select"
            v-model="selectedDriver"
            class="form-control"
          >
            <option
              :value="null"
              disabled
            >
              -- Sélectionner un livreur/pickupeur --
            </option>
            <option
              v-for="driver in drivers"
              :key="driver.id"
              :value="driver"
            >
              {{ driver.username }} - {{ getRoleLabel(driver.role) }} ({{ driver.phone || 'N/A' }})
              <span v-if="isCurrentAssigneeOption(driver)"> (Déjà assigné)</span>
            </option>
          </select>
        </div>

        <div
          v-if="isAssigning"
          class="text-center mt-3"
        >
          <b-spinner
            variant="primary"
            small
          />
          <span class="ml-2">Assignation en cours...</span>
        </div>
      </div>
    </b-modal>

    <!-- Bulk Driver Assignment Modal -->
    <b-modal
      v-model="showBulkAssignModal"
      title="Assigner des expéditions"
      title-class="text-primary"
      ok-title="Assigner"
      ok-variant="primary"
      cancel-title="Annuler"
      cancel-variant="outline-secondary"
      :ok-disabled="!selectedDriver || isAssigningBulk || selectedPickups.length === 0 || isSameBulkAssigneeSelected"
      :cancel-disabled="isAssigningBulk"
      no-close-on-backdrop
      @ok.prevent="assignDriverBulk"
      @cancel="closeBulkAssignModal"
    >
      <div>
        <div class="bg-light p-3 rounded mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="font-weight-bold">Expéditions sélectionnées</span>
            <b-badge variant="primary">
              {{ selectedPickups.length }}
            </b-badge>
          </div>
          <div class="bulk-confirm-list">
            <div
              v-for="pickup in selectedPickups"
              :key="pickup.id"
              class="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
            >
              <div class="text-left">
                <span class="font-weight-bold">Expédition #{{ pickup.id }}</span>
                <br>
                <small class="text-muted">{{ pickup.orders_count || 0 }} commandes</small>
                <div
                  v-if="pickup.assigned_to"
                  class="text-muted"
                >
                  <small>Assigné à {{ pickup.assigned_to.username }}</small>
                </div>
              </div>
              <b-badge :variant="getStatusVariant(pickup.status)">
                {{ getStatusText(pickup.status) }}
              </b-badge>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label
            for="driver-select-bulk"
            class="font-weight-bold"
          >Sélectionner un livreur</label>
          <div
            v-if="isLoadingDrivers"
            class="text-center py-3"
          >
            <b-spinner
              variant="primary"
              small
            />
            <span class="ml-2">Chargement des livreurs...</span>
          </div>
          <select
            v-else
            id="driver-select-bulk"
            v-model="selectedDriver"
            class="form-control"
          >
            <option
              :value="null"
              disabled
            >
              -- Sélectionner un livreur/pickupeur --
            </option>
            <option
              v-for="driver in drivers"
              :key="driver.id"
              :value="driver"
            >
              {{ driver.username }} - {{ getRoleLabel(driver.role) }} ({{ driver.phone || 'N/A' }})
              <span v-if="isCurrentBulkAssigneeOption(driver)"> (Déjà assigné)</span>
            </option>
          </select>
        </div>

        <div
          v-if="isAssigningBulk"
          class="text-center mt-3"
        >
          <b-spinner
            variant="primary"
            small
          />
          <span class="ml-2">Assignation en cours...</span>
        </div>
      </div>
    </b-modal>

    <!-- Pickup History Modal -->
    <b-modal
      v-model="showHistoryModal"
      title="Historique du pickup"
      title-class="text-primary"
      ok-only
      ok-title="Fermer"
      ok-variant="outline-secondary"
      size="lg"
      @hidden="resetHistoryModal"
    >
      <div
        v-if="historyPickup"
        class="bg-light p-3 rounded mb-2"
      >
        <div class="d-flex justify-content-between align-items-center">
          <span class="font-weight-bold">Pickup #{{ historyPickup.id }}</span>
          <b-badge :variant="getStatusVariant(historyPickup.status)">
            {{ getStatusText(historyPickup.status) }}
          </b-badge>
        </div>
        <div class="text-muted">
          <small>
            Fournisseur: {{ (historyPickup.supplier && historyPickup.supplier.username) || 'N/A' }}
            · {{ historyPickup.orders_count || 0 }} commandes
          </small>
        </div>
      </div>

      <div
        v-if="isHistoryLoading"
        class="text-center py-3"
      >
        <b-spinner
          variant="primary"
          small
        />
        <span class="ml-2">Chargement de l'historique...</span>
      </div>

      <div
        v-else-if="historyError"
        class="alert alert-danger mb-0"
      >
        {{ historyError }}
      </div>

      <div v-else>
        <div
          v-if="pickupHistory.length === 0"
          class="text-center text-muted py-3"
        >
          Aucun historique disponible pour ce pickup.
        </div>
        <div
          v-else
          class="history-list timeline"
        >
          <div
            v-for="entry in pickupHistory"
            :key="entry.id"
            class="history-item timeline-item"
          >
            <div class="timeline-marker">
              <span class="timeline-dot" />
            </div>
            <div class="timeline-content">
              <div class="history-meta">
                <div class="d-flex align-items-center flex-wrap">
                  <b-badge
                    variant="light-primary"
                    class="mr-2 mb-1"
                  >
                    {{ entry.action_type_display || entry.action_type }}
                  </b-badge>
                  <span class="text-muted">
                    {{ entry.done_by_name || 'Système' }}
                  </span>
                </div>
                <small class="text-muted">{{ formatFn(entry.created_at) }}</small>
              </div>
              <div class="history-message">
                {{ entry.message || 'Action enregistrée' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar, BPagination, BButton, BTooltip,
  BRow, BCol, BCard, BSpinner, BFormSelect, BModal, BBadge,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'PickupIndex',
  components: {
    VueGoodTable,
    BAvatar,
    BPagination,
    BButton,
    BTooltip,
    BRow,
    BCol,
    BCard,
    BSpinner,
    BFormSelect,
    BModal,
    BBadge,
  },

  data() {
    return {
      // Bulk select feature
      showAdvancedFilters: false,
      quickFilters: [
        {
          key: 'today', label: 'Aujourd\'hui', icon: 'CalendarIcon', style: 'filter-today',
        },
        {
          key: 'week', label: 'Cette semaine', icon: 'CalendarIcon', style: 'filter-week',
        },
        {
          key: 'month', label: 'Ce mois', icon: 'CalendarIcon', style: 'filter-month',
        },
        // {
        //   key: 'pending', label: 'En attente', icon: 'ClockIcon', style: 'filter-pending',
        // },
        // {
        //   key: 'confirmed', label: 'Confirmés', icon: 'CheckCircleIcon', style: 'filter-confirmed',
        // },
      ],
      /* statusOptions: [
        { label: 'En attente', value: 'pending' },
        { label: 'Confirmé', value: 'confirmed' },
        { label: 'En transit', value: 'in_transit' },
        { label: 'Livré', value: 'delivered' },
        { label: 'Annulé', value: 'cancelled' },
      ], */
      filters: {
        reference: '',
        status: null,
        dateFrom: null,
        dateTo: null,
        supplier: null,
        minAmount: '',
        maxAmount: '',
      },
      appliedFilters: [],
      // Date picker configuration
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1,
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
        labelCalendar: 'Calendrier',
        labelNav: 'Navigation du calendrier',
      },
      // Core data with sensible defaults
      pickups: [],
      count: 0,
      currentPage: 1,
      pageLength: 100,
      isLoading: true,
      error: null,
      user: {},
      suppliers: [],
      searchTimer: null,
      activeQuickFilter: null,

      // Confirmation modal
      showConfirmationModal: false,
      pickupToConfirm: null,
      isConfirming: false,

      // Bulk confirmation modal
      showBulkConfirmationModal: false,
      isBulkConfirming: false,
      confirmablePickups: [],
      nonConfirmablePickups: [],
      bulkConfirmTotal: 0,

      // Driver assignment modal
      showAssignModal: false,
      pickupToAssign: null,
      selectedDriver: null,
      drivers: [],
      isAssigning: false,
      isLoadingDrivers: false,
      // Bulk assignment
      showBulkAssignModal: false,
      isAssigningBulk: false,
      revokingPickupId: null,
      acceptingPickupId: null,
      startingPickupId: null,
      selectedPickups: [],
      // History modal
      showHistoryModal: false,
      historyPickup: null,
      pickupHistory: [],
      isHistoryLoading: false,
      historyError: null,
      selectOptions: {
        enabled: false,
        selectOnCheckboxOnly: true,
        selectionInfoClass: 'custom-selection-info-styled',
        selectionText: 'pickups sélectionnés',
        clearSelectionText: 'Tout désélectionner',
        disableSelectInfo: false,
      },
    }
  },

  computed: {
    tableColumns() {
      return [
        {
          label: 'Pickup',
          field: 'id',
          width: '120px',
          sortable: true,
        },
        {
          label: 'Fournisseur',
          field: 'supplier',
          width: '180px',
          sortable: true,
        },
        {
          label: 'Commandes',
          field: 'orders_count',
          width: '100px',
          sortable: true,
        },
        {
          label: 'Statut',
          field: 'status',
          width: '130px',
          sortable: true,
        },
        {
          label: 'Assigné à',
          field: 'assigned_to',
          width: '150px',
          sortable: true,
        },
        {
          label: 'Montants',
          field: 'financial_summary',
          width: '160px',
          sortable: false,
        },
        {
          label: 'Date',
          field: 'created_at',
          width: '100px',
          sortable: true,
          formatFn: this.formatFn,
        },
        {
          label: 'Actions',
          field: 'actions',
          width: '140px',
          sortable: false,
        },
      ]
    },

    hasActiveFilters() {
      return Object.keys(this.filters).some(key => {
        const value = this.filters[key]
        if (key === 'supplier') {
          return value !== null && value !== undefined
        }
        return value !== null && value !== undefined && value !== '' && value !== false
      })
    },

    /* statusSelectOptions() {
      return this.statusOptions.map(option => ({
        value: option.value,
        text: option.label,
      }))
    }, */

    averagePickupValue() {
      if (this.pickups.length === 0) return '0.000'
      const total = this.pickups.reduce((sum, p) => sum + parseFloat(p.total_price_ttc || 0), 0)
      return (total / this.pickups.length).toFixed(3)
    },

    totalTTC() {
      return this.pickups.reduce((sum, p) => sum + parseFloat(p.total_price_ttc || 0), 0).toFixed(3)
    },

    totalOrders() {
      return this.pickups.reduce((sum, p) => sum + (p.orders_count || 0), 0)
    },

    canAssignPermission() {
      return this.user.role === 'admin' || this.user.role === 'manager'
    },

    canBulkAssign() {
      return this.canAssignPermission && this.selectedPickups.length > 0
    },

    isSameAssigneeSelected() {
      return !!this.pickupToAssign?.assigned_to?.id
        && this.selectedDriver?.id === this.pickupToAssign.assigned_to.id
    },

    isSameBulkAssigneeSelected() {
      return this.selectedPickups.length === 1
        && !!this.selectedPickups[0]?.assigned_to?.id
        && this.selectedDriver?.id === this.selectedPickups[0].assigned_to.id
    },

    // Bulk confirmation computed properties
    /* canBulkConfirm() {
      return this.user.role === 'supplier'
             && this.selectedPickups.length > 0
             && this.selectedPickups.some(pickup => this.canConfirm(pickup))
    },

    selectedConfirmablePickups() {
      return this.selectedPickups.filter(pickup => this.canConfirm(pickup))
    },

    selectedNonConfirmablePickups() {
      return this.selectedPickups.filter(pickup => !this.canConfirm(pickup))
    }, */
  },

  created() {
    this.user = storeAuth.state.user
    this.restoreFilters()
    this.fetchPickups()
    if (this.user.role === 'admin') {
      this.getSuppliers()
    }
    if (this.user.role === 'admin' || this.user.role === 'manager') {
      this.selectOptions.enabled = true
    }
  },

  mounted() {
    window.addEventListener('beforeunload', this.saveFilters)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.saveFilters)
  },

  methods: {
    // Save current filters to session storage
    saveFilters() {
      sessionStorage.setItem('pickupFilters', JSON.stringify({
        filters: this.filters,
        page: this.currentPage,
      }))
    },

    // Restore filters from session storage
    restoreFilters() {
      try {
        const savedData = sessionStorage.getItem('pickupFilters')
        if (savedData) {
          const data = JSON.parse(savedData)
          this.filters = { ...this.filters, ...data.filters }
          this.currentPage = data.page || 1
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error restoring filters:', e)
      }
    },

    // Apply filters and update applied filters display
    async applyFilters() {
      this.currentPage = 1
      this.updateAppliedFilters()
      this.saveFilters()
      await this.fetchPickups()
    },

    // Update applied filters display
    updateAppliedFilters() {
      this.appliedFilters = []

      Object.keys(this.filters).forEach(key => {
        const value = this.filters[key]
        let label = key
        let displayValue = value

        // Skip empty values
        if (value === null || value === undefined || value === '' || value === false) {
          return
        }

        // Format specific filter types
        switch (key) {
          case 'supplier':
            if (value && value.username) {
              label = 'Fournisseur'
              displayValue = value.username
            }
            break
          case 'status':
            if (value && value.text) {
              label = 'Statut'
              displayValue = value.text
            }
            break
          case 'dateFrom':
            label = 'Date début'
            displayValue = this.formatDate(value)
            break
          case 'dateTo':
            label = 'Date fin'
            displayValue = this.formatDate(value)
            break
          case 'reference':
            label = 'Référence'
            break
          case 'minAmount':
            label = 'Montant min'
            displayValue = `${value} TND`
            break
          case 'maxAmount':
            label = 'Montant max'
            displayValue = `${value} TND`
            break
          default:
            label = this.formatFilterLabel(key)
        }

        if (displayValue) {
          this.appliedFilters.push({
            key,
            label,
            value: displayValue,
          })
        }
      })
    },

    // Format filter labels
    formatFilterLabel(key) {
      const labelMap = {
        reference: 'Référence',
        status: 'Statut',
        dateFrom: 'Date début',
        dateTo: 'Date fin',
        supplier: 'Fournisseur',
        minAmount: 'Montant min',
        maxAmount: 'Montant max',
      }
      return labelMap[key] || key
    },

    // Format date for display
    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    },

    // Remove specific filter
    removeFilter(filterKey) {
      if (filterKey === 'supplier' || filterKey === 'status') {
        this.filters[filterKey] = null
      } else {
        this.filters[filterKey] = filterKey.includes('Amount') ? '' : null
      }
      this.applyFilters()
    },

    // Clear all filters
    clearFilters() {
      this.filters = {
        reference: '',
        status: null,
        dateFrom: null,
        dateTo: null,
        supplier: null,
        minAmount: '',
        maxAmount: '',
      }
      this.activeQuickFilter = null
      this.appliedFilters = []
      this.showAdvancedFilters = false
      this.applyFilters()
    },

    // Quick filter functionality
    isQuickFilterActive(key) {
      return this.activeQuickFilter === key
    },

    applyQuickFilter(filter) {
      if (this.activeQuickFilter === filter.key) {
        this.activeQuickFilter = null
        this.clearFilters()
        return
      }

      this.activeQuickFilter = filter.key
      const now = new Date()
      const [today] = now.toISOString().split('T')

      switch (filter.key) {
        case 'today':
          this.filters.dateFrom = today
          this.filters.dateTo = today
          break
        case 'week': {
          const weekStart = new Date(now)
          weekStart.setDate(now.getDate() - now.getDay())
          const [weekStartDate] = weekStart.toISOString().split('T')
          this.filters.dateFrom = weekStartDate
          this.filters.dateTo = today
          break
        }
        case 'month': {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
          const [monthStartDate] = monthStart.toISOString().split('T')
          this.filters.dateFrom = monthStartDate
          this.filters.dateTo = today
          break
        }
        case 'pending':
          this.filters.status = { value: 'pending', text: 'En attente' }
          break
        case 'confirmed':
          this.filters.status = { value: 'confirmed', text: 'Confirmé' }
          break
        default:
          break
      }

      this.applyFilters()
    },

    // Fetch pickups from API
    async fetchPickups() {
      this.isLoading = true
      this.error = null

      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageLength,
        }

        // Add filters to params
        Object.keys(this.filters).forEach(key => {
          const value = this.filters[key]
          if (value !== null && value !== '' && value !== undefined) {
            if (key === 'supplier') {
              params.supplier = value?.id || value
            } else if (key === 'status') {
              params.status = value?.value || value
            } else {
              params[key] = value
            }
          }
        })

        const { data } = await axios.get('/api/pickups/', { params })
        this.pickups = data.results || []
        this.count = data.count || 0
      } catch (error) {
        this.error = 'Erreur lors du chargement des données. Veuillez réessayer.'
        this.showToast('danger', 'bottom-right', 'Erreur lors du chargement des pickups')
        // eslint-disable-next-line no-console
        console.error('Error fetching pickups:', error)
      } finally {
        this.isLoading = false
      }
    },

    // Get suppliers for admin users
    async getSuppliers() {
      try {
        const { data } = await axios.get('/api/auth/users/suppliers/')
        this.suppliers = Array.isArray(data) ? data : []
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading suppliers:', error)
        this.showToast('danger', 'bottom-right', 'Erreur lors du chargement des fournisseurs')
      }
    },

    // Handle pagination
    pageNext(currentPage) {
      this.currentPage = currentPage
      this.fetchPickups()
      this.saveFilters()
    },

    // Refresh data
    refreshData() {
      this.fetchPickups()
    },

    onSelectionChanged({ selectedRows }) {
      this.selectedPickups = selectedRows || []
    },

    // Selection management
    // Bulk select feature removed

    // Status and formatting methods
    getStatusClass(status) {
      const classes = {
        pending: 'bg-warning',
        confirmed: 'bg-info',
        assigned: 'bg-primary',
        in_progress: 'bg-primary',
        collected: 'bg-success',
        partial: 'bg-warning',
        cancelled: 'bg-danger',
      }
      return classes[status] || 'bg-secondary'
    },

    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        confirmed: 'info',
        assigned: 'primary',
        accepted: 'primary',
        in_progress: 'info',
        collected: 'success',
        partial: 'warning',
        cancelled: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    getStatusIcon(status) {
      const icons = {
        pending: 'ClockIcon',
        confirmed: 'CheckCircleIcon',
        assigned: 'UserCheckIcon',
        in_progress: 'TruckIcon',
        collected: 'PackageIcon',
        partial: 'AlertTriangleIcon',
        cancelled: 'XCircleIcon',
      }
      return icons[status] || 'HelpCircleIcon'
    },

    getStatusText(status) {
      const texts = {
        pending: 'En attente',
        confirmed: 'Confirmé',
        assigned: 'Assigné',
        accepted: 'Accepté',
        in_progress: 'En cours',
        collected: 'Collecté',
        partial: 'Partiel',
        cancelled: 'Annulé',
      }
      return texts[status] || 'Inconnu'
    },

    isCurrentAssigneeOption(driver) {
      return !!this.pickupToAssign?.assigned_to?.id
        && driver?.id === this.pickupToAssign.assigned_to.id
    },

    isCurrentBulkAssigneeOption(driver) {
      return this.selectedPickups.length === 1
        && !!this.selectedPickups[0]?.assigned_to?.id
        && driver?.id === this.selectedPickups[0].assigned_to.id
    },

    // Driver assignment methods
    canAssign(pickup) {
      if (!pickup) return false
      const isAdmin = ['admin', 'superadmin', 'admin_developer'].includes(this.user.role)
      if (pickup.status === 'accepted') {
        return isAdmin
      }
      return ['pending', 'confirmed', 'assigned'].includes(pickup.status)
    },

    canRevokeAcceptance(pickup) {
      return ['admin', 'superadmin', 'admin_developer'].includes(this.user.role)
        && pickup.status === 'accepted'
    },

    isDriver() {
      return ['delivery_man', 'pickupeur'].includes(this.user.role)
    },

    canAcceptPickup(pickup) {
      return this.isDriver()
        && pickup?.assigned_to?.id === this.user.id
        && pickup.status === 'assigned'
    },

    canStartPickup(pickup) {
      return this.isDriver()
        && pickup?.assigned_to?.id === this.user.id
        && pickup.status === 'accepted'
    },

    async revokeAcceptance(pickup) {
      if (!pickup?.id || this.revokingPickupId === pickup.id) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Révoquer l'acceptation de ce pickup ?",
        {
          title: 'Confirmation',
          okTitle: 'Révoquer',
          cancelTitle: 'Annuler',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return

      this.revokingPickupId = pickup.id
      try {
        await axios.post(`/api/pickups/${pickup.id}/revoke-acceptance/`)
        this.$bvToast.toast('Acceptation révoquée', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        await this.fetchPickups()
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error || 'Erreur lors de la révocation', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.revokingPickupId = null
      }
    },

    async acceptPickup(pickup) {
      if (!pickup?.id || this.acceptingPickupId === pickup.id) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Accepter cette collecte ?',
        {
          title: 'Confirmation',
          okTitle: 'Accepter',
          cancelTitle: 'Annuler',
          okVariant: 'primary',
          centered: true,
        },
      )
      if (!confirmed) return

      this.acceptingPickupId = pickup.id
      try {
        await axios.post(`/api/pickups/${pickup.id}/accept/`)
        this.$bvToast.toast('Collecte acceptée', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        await this.fetchPickups()
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error || 'Erreur lors de l\'acceptation', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.acceptingPickupId = null
      }
    },

    async startPickup(pickup) {
      if (!pickup?.id || this.startingPickupId === pickup.id) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Démarrer la collecte ? Le statut passera à "En cours".',
        {
          title: 'Confirmation',
          okTitle: 'Démarrer',
          cancelTitle: 'Annuler',
          okVariant: 'success',
          centered: true,
        },
      )
      if (!confirmed) return

      this.startingPickupId = pickup.id
      try {
        await axios.post(`/api/pickups/${pickup.id}/start/`)
        this.$bvToast.toast('Collecte démarrée', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        await this.fetchPickups()
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error || 'Erreur lors du démarrage', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.startingPickupId = null
      }
    },

    async openBulkAssignModal() {
      if (!this.selectedPickups.length) return
      if (this.selectedPickups.length === 1) {
        this.selectedDriver = this.selectedPickups[0]?.assigned_to || null
      } else {
        this.selectedDriver = null
      }
      this.showBulkAssignModal = true
      await this.fetchDrivers()
      if (this.selectedPickups.length === 1 && this.selectedPickups[0]?.assigned_to?.id) {
        const matchedDriver = this.drivers.find(
          driver => driver.id === this.selectedPickups[0].assigned_to.id,
        )
        this.selectedDriver = matchedDriver || this.selectedDriver
      }
    },

    closeBulkAssignModal() {
      this.showBulkAssignModal = false
      this.selectedDriver = null
    },

    async openAssignModal(pickup) {
      this.pickupToAssign = pickup
      this.selectedDriver = pickup?.assigned_to || null
      this.showAssignModal = true
      await this.fetchDrivers()
      if (pickup?.assigned_to?.id) {
        const matchedDriver = this.drivers.find(driver => driver.id === pickup.assigned_to.id)
        this.selectedDriver = matchedDriver || this.selectedDriver
      }
    },

    closeAssignModal() {
      this.showAssignModal = false
      this.pickupToAssign = null
      this.selectedDriver = null
    },

    async fetchDrivers() {
      this.isLoadingDrivers = true
      try {
        const { data } = await axios.get('/api/auth/users/assignable-pickup-drivers/')
        this.drivers = Array.isArray(data) ? data : data.results || []
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching drivers:', error)
        this.$bvToast.toast('Erreur lors du chargement des livreurs', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isLoadingDrivers = false
      }
    },

    async assignDriver() {
      if (!this.pickupToAssign || !this.selectedDriver) return

      this.isAssigning = true
      try {
        // Use the correct pickup assign endpoint
        await axios.post(`/api/pickups/${this.pickupToAssign.id}/assign/`, {
          driver_id: this.selectedDriver.id,
        })
        this.$bvToast.toast(`Pickup #${this.pickupToAssign.id} assigné à ${this.selectedDriver.username}`, {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        this.closeAssignModal()
        this.fetchPickups()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error assigning pickup:', error)
        this.$bvToast.toast(error.response?.data?.message || 'Erreur lors de l\'assignation', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isAssigning = false
      }
    },

    async assignDriverBulk() {
      if (!this.selectedDriver || this.selectedPickups.length === 0) return

      this.isAssigningBulk = true
      try {
        const pickupIds = this.selectedPickups.map(pickup => pickup.id)
        const { data } = await axios.post('/api/pickups/bulk-assign/', {
          pickup_ids: pickupIds,
          driver_id: this.selectedDriver.id,
        })

        this.$bvToast.toast(
          data?.message || `Pickups assignés à ${this.selectedDriver.username}`,
          {
            title: 'Succès',
            variant: 'success',
            solid: true,
          },
        )
        this.closeBulkAssignModal()
        this.fetchPickups()
        this.selectedPickups = []
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error bulk assigning pickups:', error)
        this.$bvToast.toast(error.response?.data?.message || 'Erreur lors de l\'assignation en lot', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isAssigningBulk = false
      }
    },

    async openHistoryModal(pickup) {
      if (!pickup?.id) return
      this.showHistoryModal = true
      this.historyPickup = pickup
      this.pickupHistory = []
      this.isHistoryLoading = true
      this.historyError = null
      try {
        const { data } = await axios.get(`/api/pickups/${pickup.id}/history/`)
        this.pickupHistory = Array.isArray(data?.results) ? data.results : data || []
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pickup history:', error)
        this.historyError = 'Erreur lors du chargement de l\'historique.'
      } finally {
        this.isHistoryLoading = false
      }
    },

    resetHistoryModal() {
      this.showHistoryModal = false
      this.historyPickup = null
      this.pickupHistory = []
      this.isHistoryLoading = false
      this.historyError = null
    },

    getRoleLabel(role) {
      const labels = {
        delivery_man: 'Livreur',
        pickupeur: 'Pickupeur',
        admin: 'Admin',
        manager: 'Manager',
        supplier: 'Fournisseur',
      }
      return labels[role] || role
    },

    formatRelativeDate(date) {
      if (!date) return 'Date inconnue'

      try {
        const now = new Date()
        const pickupDate = new Date(date)
        const diffTime = Math.abs(now - pickupDate)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

        if (diffDays === 0) return "Aujourd'hui"
        if (diffDays === 1) return 'Hier'
        if (diffDays < 7) return `Il y a ${diffDays} jours`
        return pickupDate.toLocaleDateString('fr-FR')
      } catch (e) {
        return 'Date invalide'
      }
    },

    formatPrice(price) {
      return parseFloat(price || 0).toFixed(3)
    },

    getInitials(name) {
      if (!name) return 'U'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
        .substring(0, 2)
    },

    // Permission checks
    /* canEdit(pickup) {
      // Only suppliers can edit
      if (this.user.role !== 'supplier') {
        return false
      }

      // Only pickup owner can edit
      if (pickup.supplier?.id !== this.user.id) {
        return false
      }

      // Only pending pickups can be edited by suppliers
      return pickup.status === 'pending'
    },

    canConfirm(pickup) {
      // Only suppliers can confirm
      if (this.user.role !== 'supplier') {
        return false
      }

      // Only pickup owner can confirm
      if (pickup.supplier?.id !== this.user.id) {
        return false
      }

      // Only pending pickups can be confirmed
      return pickup.status === 'pending'
    }, */

    // Confirmation methods
    /* showConfirmDialog(pickup) {
      this.pickupToConfirm = pickup
      this.showConfirmationModal = true
    },

    cancelConfirmation() {
      this.pickupToConfirm = null
      this.showConfirmationModal = false
    },

    async confirmPickup() {
      if (!this.pickupToConfirm) return

      this.isConfirming = true

      try {
        const response = await this.$http.post(`/api/pickups/confirm/${this.pickupToConfirm.id}/`)

        this.showToast('success', 'bottom-right', response.data.message || 'Pickup confirmé avec succès')

        // Refresh the list to get updated data
        await this.fetchPickups()

        // Close modal and reset state
        this.showConfirmationModal = false
        this.pickupToConfirm = null
      } catch (error) {
        console.error('Error confirming pickup:', error)

        let errorMessage = 'Erreur lors de la confirmation du pickup'
        if (error.response?.status === 400) {
          errorMessage = error.response.data?.detail || 'Ce pickup ne peut pas être confirmé'
        } else if (error.response?.status === 403) {
          errorMessage = 'Vous n\'avez pas les permissions pour confirmer ce pickup'
        } else if (error.response?.status === 404) {
          errorMessage = 'Pickup introuvable'
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail
        }

        this.showToast('danger', 'bottom-right', errorMessage)
      } finally {
        this.isConfirming = false
      }
    },

    // Bulk confirmation methods
    showBulkConfirmDialog() {
      this.confirmablePickups = this.selectedPickups.filter(pickup => this.canConfirm(pickup))
      this.nonConfirmablePickups = this.selectedPickups.filter(pickup => !this.canConfirm(pickup))
      this.bulkConfirmTotal = this.confirmablePickups.reduce((sum, p) => sum + parseFloat(p.total_price_ttc || 0), 0)

      if (this.confirmablePickups.length === 0) {
        this.showToast('warning', 'bottom-right', 'Aucun pickup confirmable sélectionné')
        return
      }

      this.showBulkConfirmationModal = true
    },

    cancelBulkConfirmation() {
      this.showBulkConfirmationModal = false
      this.confirmablePickups = []
      this.nonConfirmablePickups = []
      this.bulkConfirmTotal = 0
    },

    async bulkConfirmPickups() {
      if (this.confirmablePickups.length === 0) return

      this.isBulkConfirming = true

      try {
        const pickupIds = this.confirmablePickups.map(pickup => pickup.id)
        await this.$http.post('/api/pickups/bulk-confirm/', {
          pickup_ids: pickupIds,
        })

        // Refresh the list to get updated data
        await this.fetchPickups()

        // Close modal and reset state
        this.showBulkConfirmationModal = false
        this.confirmablePickups = []
        this.nonConfirmablePickups = []
        this.bulkConfirmTotal = 0

        // Clear selection after successful operation
        this.clearSelection()
      } catch (error) {
        console.error('Error bulk confirming pickups:', error)
      } finally {
        this.isBulkConfirming = false
      }
    },

    getReasonNotConfirmable(pickup) {
      if (pickup.supplier?.id !== this.user.id) {
        return 'Pas votre pickup'
      }
      if (pickup.status !== 'pending') {
        return `Statut: ${this.getStatusText(pickup.status)}`
      }
      if (!pickup.orders_count || pickup.orders_count === 0) {
        return 'Aucune commande'
      }
      return 'Raison inconnue'
    }, */

    // Enhanced toast method for consistent positioning
    showToast(variant, position = 'bottom-right', message, autoHideDelay = 5000) {
      this.$bvToast.toast(message, {
        title: this.getToastTitle(variant),
        variant,
        toaster: position,
        solid: true,
        autoHideDelay,
        appendToast: false,
      })
    },

    getToastTitle(variant) {
      const titles = {
        success: 'Succès',
        danger: 'Erreur',
        warning: 'Attention',
        info: 'Information',
      }
      return titles[variant] || 'Notification'
    },

    formatFn(date) {
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';

// Force immediate font loading
html {
  font-family: 'Poppins', sans-serif;
}

body {
  font-family: 'Poppins', sans-serif !important;
}

* {
  font-family: 'Poppins', sans-serif !important;
}

.vgt-table,
.vgt-table th,
.vgt-table td,
.vgt-table .vgt-inner-wrap,
.vue-good-table {
  font-family: 'Poppins', sans-serif !important;
}

/* Modern App Container */
.pickups-app {
  min-height: 100vh;
  background: #f8fafc;
  padding: 1rem;
  font-family: 'Poppins', sans-serif;
}

/* Modern Header with Glass Effect */
.app-header {
  background: white;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
}

.app-header.small-header {
  padding: 0.75rem 1.5rem;
}

.small-title {
  font-size: 1.25rem;
  margin-bottom: 0.2rem;
}

.small-subtitle {
  font-size: 0.85rem;
  margin-bottom: 0;
}

.stats-card.small-stats {
  padding: 0.5rem 1rem;
  min-width: auto;
}

.stats-card.small-stats .stat-value {
  font-size: 1.25rem;
}

.stats-card.small-stats .stat-label {
  font-size: 0.7rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-logo {
  width: 80px;
  height: 60px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.15);
  border: 2px solid #f1f5f9;
  transition: all 0.3s ease;
  cursor: pointer;
}

.header-logo:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(10, 38, 52, 0.25);
  border-color: #0A2634;
}

.brand-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: filter 0.3s ease;
}

.header-logo:hover .brand-logo {
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.15));
}

.header-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  letter-spacing: -0.5px;
}

.header-subtitle {
  color: #6b7280;
  margin: 0.5rem 0 0 0;
  font-size: 1.1rem;
  font-weight: 500;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.stats-card {
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem 2rem;
  gap: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-item {
  text-align: center;
}

.stat-value {
  display: block;
  font-size: 2.2rem;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.history-list {
  max-height: 420px;
  overflow-y: auto;
}

.history-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.history-item:last-child {
  border-bottom: none;
}

.history-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.35rem;
}

.timeline {
  position: relative;
  padding-left: 0.25rem;
}

.timeline-item {
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 0.75rem;
  position: relative;
}

.timeline-item::before {
  content: '';
  position: absolute;
  left: 11px;
  top: 18px;
  bottom: -12px;
  width: 2px;
  background: linear-gradient(180deg, #dbeafe, #e5e7eb);
}

.timeline-item:last-child::before {
  display: none;
}

.timeline-marker {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.2rem;
}

.timeline-dot {
  width: 10px;
  height: 10px;
  background: #2563eb;
  border-radius: 50%;
  box-shadow: 0 0 0 4px #dbeafe;
}

.timeline-content {
  padding-bottom: 0.35rem;
}

.history-message {
  color: #111827;
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: #d1d5db;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.action-btn {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  font-size: 0.9rem;
  text-decoration: none; /* Ensure links are styled correctly */
}

.action-btn.primary {
  background: #0A2634;
  color: white;
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.3);
}

.action-btn.secondary {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.action-btn.secondary:hover {
  background: #f8fafc;
  color: #0f172a;
  border-color: #cbd5e1;
}

.action-btn.danger-btn {
  background: #ED1919;
  color: white;
  box-shadow: 0 2px 8px rgba(237, 25, 25, 0.3);
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.action-btn.danger-btn:hover:not(:disabled) {
  background: #c70000;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.4);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Modern Search & Filters Panel */
.filters-panel {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.search-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.search-input-wrapper {
  flex: 1;
  position: relative;
}

.search-input {
  width: 100%;
  padding: 1rem 1rem 1rem 3.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  background: white;
  transition: all 0.3s ease;
  color: #1f2937;
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-input:focus {
  outline: none;
  border-color: #0A2634;
  box-shadow: 0 0 0 3px rgba(10, 38, 52, 0.1);
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}

.clear-search {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0.25rem;
  border-radius: 50%;
}

.clear-search:hover {
  color: #0A2634;
  background: rgba(10, 38, 52, 0.1);
}

.filter-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #1f2937;
  position: relative;
}

.filter-toggle:hover,
.filter-toggle.active {
  background: #0A2634;
  color: white;
  border-color: #0A2634;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.3);
}

.filter-count {
  background: #1f2937;
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  position: absolute;
  top: -5px;
  right: -5px;
}

.filter-toggle.active .filter-count {
  background: white;
  color: #0A2634;
}

/* Expandable Filters */
.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.filter-active-indicator {
  color: #0A2634;
  font-size: 0.75rem;
  font-weight: 700;
}

.filter-select,
.filter-input {
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  color: #1f2937;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #0A2634;
  box-shadow: 0 0 0 3px rgba(10, 38, 52, 0.1);
}

.filter-select.filter-active,
.filter-input.filter-active {
  border-color: #0A2634;
  background: #fef2f2;
  box-shadow: 0 0 0 3px rgba(10, 38, 52, 0.1);
}

/* Quick Filters */
.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.quick-filter-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  background: white;
  color: #1f2937;
  position: relative;
}

.filter-today { border-color: #0A2634; color: #0A2634; }
.filter-week { border-color: #059669; color: #059669; }
.filter-month { border-color: #d97706; color: #d97706; }
.filter-pending { border-color: #f59e0b; color: #f59e0b; }
.filter-confirmed { border-color: #10b981; color: #10b981; }

.quick-filter-btn:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.quick-filter-btn.quick-filter-active {
  background: var(--color, #0A2634);
  color: white;
  border-color: var(--color, #0A2634);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.quick-filter-check {
  position: absolute;
  top: -5px;
  right: -5px;
  background: white;
  color: #10b981;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
}

/* Applied Filters */
.applied-filters {
  padding: 1rem;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid rgba(10, 38, 52, 0.2);
}

.applied-filters-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.75rem;
}

.applied-filters-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.applied-filter-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #0A2634;
  color: white;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.applied-filter-badge:hover {
  background: #c70000;
  transform: scale(1.05);
}

/* Selection Action Bar */
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 16px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.action-bar.selection-active {
  background: linear-gradient(135deg, rgba(10, 38, 52, 0.95) 0%, rgba(29, 29, 27, 0.9) 100%);
  color: white;
  border: 2px solid rgba(10, 38, 52, 0.3);
  box-shadow: 0 8px 32px rgba(10, 38, 52, 0.2);
}

.action-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.action-right {
  display: flex;
  gap: 1rem;
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.selection-count {
  background: #f59e0b;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.9rem;
}

.selection-text {
  font-weight: 500;
  color: #ffffff; /* Changed from #92400e to white for better contrast */
}

/* Status dot styling - keep existing */
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;

  &.bg-warning {
    background-color: #ffc107 !important;
  }
  &.bg-info {
    background-color: #17a2b8 !important;
  }
  &.bg-primary {
    background-color: #007bff !important;
  }
  &.bg-success {
    background-color: #28a745 !important;
  }
  &.bg-danger {
    background-color: #dc3545 !important;
  }
  &.bg-secondary {
    background-color: #6c757d !important;
  }
}

/* Enhanced styling for no results card */
.no-results-card {
  border: 2px dashed #e3e6f0;
  background-color: #f8f9fc;

  .feather-icon {
    opacity: 0.5;
  }
}

/* Loading, Empty, and Error States */
.loading-container,
.empty-state,
.error-state {
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.loading-text {
  color: #6b7280;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
}

.empty-icon,
.error-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;
  background: rgba(10, 38, 52, 0.1);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0A2634;
}

.empty-title,
.error-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
  letter-spacing: -0.025em;
}

.empty-description,
.error-description {
  color: #6b7280;
  margin-bottom: 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  font-size: 1.1rem;
  line-height: 1.6;
}

.empty-action,
.error-action {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #0A2634 0%, #c70000 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.3);
}

.empty-action:hover,
.error-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(10, 38, 52, 0.4);
  color: white;
  text-decoration: none;
}

/* Table Container */
.table-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 0;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Enhanced Table Styling */
.vue-good-table {
  border-radius: 16px;
  overflow: hidden;
  border: none;
  background: transparent;
}

.vgt-table {
  border: 1px solid #e5e7eb;
  background: transparent;
  border-collapse: collapse;
}

/* Remove heavy strokes and unify to light gray */
.vue-good-table .vgt-table,
.vue-good-table .vgt-table thead tr,
.vue-good-table .vgt-table thead th {
  border: 1px solid #e5e7eb !important;
  box-shadow: none !important;
}

.vgt-table thead th {
  background: linear-gradient(135deg, rgba(31, 41, 55, 0.05) 0%, rgba(10, 38, 52, 0.02) 100%);
  color: #1f2937;
  font-weight: 700;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1.25rem 1rem;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
}

.vgt-table thead th:last-child {
  border-right: none;
}

.vgt-table tbody td {
  padding: 1.25rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  vertical-align: middle;
  color: #374151;
  font-weight: 500;
}

.vgt-table tbody td:last-child {
  border-right: none;
}

.vgt-table tbody tr {
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.8);
}

.vgt-table tbody tr:hover {
  background: rgba(254, 242, 242, 0.8);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Orders count pill styling (green chip, red text) */
.orders-count-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(227, 6, 19, 0.12);
  color: #ED1919;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: none;
  box-shadow: none;
  font-weight: 700;
}

.orders-count-number {
  font-size: 1.1rem;
  font-weight: 800;
  line-height: 1;
}

.orders-count-label {
  font-size: 0.85rem;
  text-transform: lowercase;
  letter-spacing: 0.2px;
}

.vgt-table tbody tr:nth-child(even) {
  background: rgba(249, 250, 251, 0.5);
}

.vgt-table tbody tr:nth-child(even):hover {
  background: rgba(254, 242, 242, 0.8);
}

/* Status Dots Enhancement */
.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.9);
}

/* Badge Enhancements */
.badge-glow {
  box-shadow: 0 4px 14px 0 rgba(10, 38, 52, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 8px;
}

/* Action Icons Enhancement - Brand Aligned */
.cursor-pointer {
  transition: all 0.2s ease;
}

.cursor-pointer:hover {
  transform: scale(1.1);
}

/* Brand-aligned Action Icons */
.feather-icon[data-feather="eye"] {
  color: #0A2634 !important; /* Brand red for view action */
}

.feather-icon[data-feather="check-circle"] {
  color: #28a745 !important; /* Keep green for confirm action */
}

.feather-icon[data-feather="edit"] {
  color: #1D1D1B !important; /* Brand black for edit action */
}

/* Enhanced action button hover effects */
.text-danger:hover .feather-icon {
  color: #c70000 !important; /* Darker red on hover */
}

.text-dark:hover .feather-icon {
  color: #000000 !important; /* Pure black on hover */
}

.text-success:hover .feather-icon {
  color: #1e7e34 !important; /* Darker green on hover */
}

/* Brand-aligned Badge Styling */
.badge-glow {
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.badge-glow:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Enhanced Status Badge Colors */
.badge-primary {
  background: linear-gradient(135deg, #0A2634 0%, #c70000 100%) !important;
  color: #ffffff !important;
  border: 2px solid #0A2634;
  box-shadow: 0 2px 8px rgba(10, 38, 52, 0.25);
}

.badge-secondary {
  background: linear-gradient(135deg, #1D1D1B 0%, #000000 100%) !important;
  color: #ffffff !important;
  border: 2px solid #1D1D1B;
  box-shadow: 0 2px 8px rgba(29, 29, 27, 0.25);
}

.badge-success {
  background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%) !important;
  color: #ffffff !important;
  border: 2px solid #28a745;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.25);
}

.badge-warning {
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%) !important;
  color: #1D1D1B !important;
  border: 2px solid #ffc107;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.25);
}

.badge-info {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%) !important;
  color: #ffffff !important;
  border: 2px solid #17a2b8;
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.25);
}

.badge-danger {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%) !important;
  color: #ffffff !important;
  border: 2px solid #dc3545;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.25);
}

/* Table Row Enhancements */
.vgt-table tbody tr {
  transition: all 0.2s ease;
  border-left: 1px solid #e5e7eb;
}

.vgt-table tbody tr:hover {
  background: rgba(10, 38, 52,  0.02) !important;
  border-left-color: #e5e7eb;
  box-shadow: none;
}

/* Status Dot Enhancement */
.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  animation: pulse 2s infinite;
}

.status-dot.status-pending {
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
  box-shadow: 0 0 0 1px rgba(255, 193, 7, 0.3), 0 0 8px rgba(255, 193, 7, 0.4);
}

.status-dot.status-confirmed {
  background: linear-gradient(135deg, #0A2634 0%, #c70000 100%);
  box-shadow: 0 0 0 1px rgba(232, 0,  0, 0.3), 0 0 8px rgba(10, 38, 52, 0.4);
}

.status-dot.status-cancelled {
   background: linear-gradient(135deg, #1D1D1B 0%, #000000 100%);
  box-shadow: 0 0 0 1px rgba(29, 29, 27, 0.3), 0 0 8px rgba(29, 29, 27, 0.4);
}

.status-dot.status-in-progress {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  box-shadow: 0 0 0 1px rgba(23, 162, 184, 0.3), 0 0 8px rgba(23, 162, 184, 0.4);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(10, 38, 52, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(10, 38, 52, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 38, 52, 0);
  }
}

/* Table Header Enhancement */
.vgt-table thead th {
  background: linear-gradient(135deg, rgba(10, 38, 52, 0.08) 0%, rgba(29, 29, 27, 0.03) 100%) !important;
  color: #1D1D1B !important;
  font-weight: 700 !important;
  font-size: 0.875rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 1rem 0.75rem !important;
  position: relative;
  border-top: 1px solid #e5e7eb !important;
  border-bottom: 1px solid #e5e7eb !important;
  border-left: 1px solid #e5e7eb !important;
  border-right: 1px solid #e5e7eb !important;
}

.vgt-table thead th:first-child {
  border-left: 1px solid #e5e7eb !important;
}

.vgt-table thead th:last-child {
  border-right: 1px solid #e5e7eb !important;
}

/* Table Cell Enhancement */
.vgt-table tbody td {
  padding: 1rem 0.75rem !important;
  font-weight: 500 !important;
  color: #1D1D1B !important;
  border-bottom: 1px solid rgba(10, 38, 52, 0.1) !important;
  vertical-align: middle !important;
}

/* Financial Values Styling */
.text-success {
  color: #28a745 !important;
  font-weight: 700 !important;
}

.text-info {
  color: #17a2b8 !important;
  font-weight: 700 !important;
}

.text-primary {
  color: #0A2634 !important;
  font-weight: 700 !important;
}

/* Avatar Enhancement */
.b-avatar {
  border: 2px solid rgba(10, 38, 52, 0.2);
  font-weight: 700;
  background: linear-gradient(135deg, rgba(10, 38, 52, 0.1) 0%, rgba(29, 29, 27, 0.05) 100%);
  color: #1D1D1B;
}

/* Table Container Enhancement */
.vgt-wrap {
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid #e5e7eb !important;
}

/* Search and Filter Enhancements */
.vgt-global-search {
  border: 2px solid rgba(10, 38, 52, 0.2) !important;
  border-radius: 8px !important;
  padding: 0.75rem 1rem !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
}

.vgt-global-search:focus {
  border-color: #0A2634 !important;
  box-shadow: 0 0 0 0.2rem rgba(10, 38, 52, 0.15) !important;
}

/* Checkbox Enhancement */
.vgt-checkbox-col input[type="checkbox"] {
  width: 18px !important;
  height: 18px !important;
  accent-color: #0A2634 !important;
  border: 2px solid rgba(10, 38, 52, 0.3) !important;
  border-radius: 4px !important;
}

.pagination-row {
  padding: 0.75rem 1.5rem;
}

.pagination .page-link {
  color: #ED1919;
  border: none;
  background: rgba(227, 6, 19, 0.1);
  border-radius: 50% !important;
  min-width: 38px;
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  transition: all 0.2s ease;
  box-shadow: none;
}

.pagination .page-link:hover {
  background: rgba(227, 6, 19, 0.18);
  transform: translateY(-1px);
}

.pagination .page-item.active .page-link {
  background: #ED1919;
  color: #ffffff;
  border: none;
  box-shadow: none;
}

.pagination .page-link:focus {
  outline: none;
  box-shadow: none;
}

/* Summary Card Enhancement */
.summary-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.summary-card .card-header {
  background: linear-gradient(135deg, rgba(10, 38, 52, 0.05) 0%, rgba(29, 29, 27, 0.02) 100%);
  border-bottom: 2px solid rgba(10, 38, 52, 0.1);
  padding: 1.5rem 2rem;
}

.summary-card .card-header h5 {
  color: #1f2937;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.summary-card .card-body {
  padding: 2rem;
}

.summary-card .text-primary {
  background: linear-gradient(135deg, #0A2634 0%, #c70000 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
  font-size: 2.5rem;
}

.summary-card .text-success {
  color: #10b981;
  font-weight: 800;
  font-size: 2.5rem;
}

.summary-card .text-info {
  color: #3b82f6;
  font-weight: 800;
  font-size: 2.5rem;
}

.summary-card .text-warning {
  color: #f59e0b;
  font-weight: 800;
  font-size: 2.5rem;
}

.summary-card .text-muted {
  color: #6b7280;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Brand Color Variables for Consistency */
:root {
  --mz-brand-red: #ED1919;
  --mz-brand-black: #1D1D1B;
  --mz-brand-red-dark: #b51414;
  --mz-brand-red-light: #ff4a4a;
  --mz-brand-black-light: #2d2d2b;
}

/* ID Column Enhancement */
.text-nowrap strong {
  color: var(--mz-brand-black);
  font-weight: 700;
}

.text-nowrap small.text-muted {
  color: #6c757d;
  font-weight: 500;
}

/* Financial Values Brand Enhancement */
.font-weight-semibold.text-primary {
  color: var(--mz-brand-red) !important;
  font-weight: 800 !important;
  font-size: 1.1rem;
}

.font-weight-semibold.text-success {
  color: #28a745 !important;
  font-weight: 800 !important;
  font-size: 1rem;
}

.strong.text-success {
  color: #28a745 !important;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(40, 167, 69, 0.1);
}

.strong.text-info {
  color: #17a2b8 !important;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(23, 162, 184, 0.1);
}

/* Supplier Information Enhancement */
.font-weight-semibold {
  color: var(--mz-brand-black);
  font-weight: 600;
  font-size: 0.95rem;
}

/* Orders Count Badge Enhancement */
.text-center .font-weight-semibold.text-primary {
  background: linear-gradient(135deg, rgba(10, 38, 52, 0.1) 0%, rgba(10, 38, 52, 0.05) 100%);
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  border: 1px solid rgba(10, 38, 52, 0.2);
  display: inline-block;
  min-width: 2.5rem;
}

/* Loading Spinner Brand Color */
.b-spinner.text-primary {
  color: var(--mz-brand-red) !important;
}

/* Modal Enhancement */
.modal-title.text-danger {
  color: var(--mz-brand-red) !important;
  font-weight: 700;
}

/* Tooltip Enhancement */
.tooltip .tooltip-inner {
  background: var(--mz-brand-black);
  color: white;
  font-weight: 500;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
}

.tooltip .arrow::before {
  border-top-color: var(--mz-brand-black);
}

/* Enhanced Loading States */
.btn.loading {
  position: relative;
  pointer-events: none;
  opacity: 0.7;
}

.btn.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 16px;
  height: 16px;
  margin: -8px 0 0 -8px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Enhanced Modal Styling */
.modal-header {
  background: linear-gradient(135deg, rgba(10, 38, 52, 0.05) 0%, rgba(29, 29, 27, 0.02) 100%);
  border-bottom: 2px solid rgba(10, 38, 52, 0.1);
}

.modal-title {
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-body {
  padding: 2rem;
  text-align: center;
}

.modal-footer {
  background: rgba(249, 250, 251, 0.5);
  border-top: 1px solid rgba(10, 38, 52, 0.1);
  padding: 1.25rem 2rem;
}

/* Enhanced Success Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.4s ease-out;
}

/* Enhanced Table Selection */
.vgt-table tbody tr.vgt-selected {
  background: rgba(10, 38, 52, 0.08) !important;
  border-left-color: #0A2634 !important;
  border-left-width: 4px !important;
}

.vgt-table tbody tr.vgt-selected:hover {
  background: rgba(10, 38, 52, 0.12) !important;
}

/* Enhanced Action Bar */
.action-bar.selection-active {
  background: linear-gradient(135deg, rgba(10, 38, 52, 0.95) 0%, rgba(29, 29, 27, 0.9) 100%);
  color: white;
  border: 2px solid rgba(10, 38, 52, 0.3);
  box-shadow: 0 8px 32px rgba(10, 38, 52, 0.2);
}

.action-bar.selection-active .selection-info {
  color: white; /* Ensure all text in selection info is white */
}

.action-bar.selection-active .selection-count {
  background: rgba(255, 255, 255, 0.2); /* Semi-transparent white background */
  color: #ffffff; /* White text */
  border: 1px solid rgba(255, 255, 255, 0.3); /* Subtle white border */
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.9rem;
}

.action-bar.selection-active .selection-text {
  color: rgba(255, 255, 255, 0.9); /* Slightly transparent white for hierarchy */
  font-weight: 500;
}

/* Enhanced button styling in selection mode */
.action-bar.selection-active .action-btn {
  color: white;
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
}

.action-bar.selection-active .action-btn.confirm {
  background: rgba(40, 167, 69, 0.9); /* Green with transparency */
  border-color: #28a745;
  color: white;
}

.action-bar.selection-active .action-btn.confirm:hover {
  background: #28a745; /* Solid green on hover */
  border-color: #1e7e34;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

.action-bar.selection-active .action-btn.secondary {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
  color: white;
}

.action-bar.selection-active .action-btn.secondary:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-1px);
}
</style>
